import { useEffect } from "react";

/**
 * Global safeguard for the well-known Radix `pointer-events: none` leak.
 *
 * Radix's DismissableLayer (used by every modal Dialog / AlertDialog /
 * DropdownMenu / Select / Popover / ContextMenu / Menubar) sets
 * `document.body.style.pointerEvents = "none"` while a modal layer is open
 * and restores the previous value when the last modal layer unmounts. The
 * restore is keyed off a *module-level singleton* (`originalBodyPointerEvents`)
 * plus a shared layer set, so when two layers open/close in overlapping ticks
 * the singleton can capture `"none"` as the "original" value — and the lock is
 * never cleared. Once that happens every app interaction underneath the
 * (now closed) overlay is dead until a full page reload: sliders won't drag,
 * gear menus won't open, long-press shows the browser's native image menu
 * instead of entering multi-select, etc.
 *
 * This was previously hand-patched dialog-by-dialog with one-tick deferred
 * closes that reset `document.body.style.pointerEvents`. Those only covered
 * specific dialogs, so the leak kept resurfacing elsewhere. This hook handles
 * it globally: mounted once near the app root, it watches `document.body` and
 * clears the lock whenever the body is stuck at `pointer-events: none` while no
 * Radix modal layer is actually open in the DOM. It never clears the lock while
 * an overlay is legitimately open, so desktop background-blocking is unchanged.
 */

/**
 * Selector matching any Radix overlay layer that can legitimately hold the
 * body `pointer-events` lock while it is mounted. If one of these is present
 * the lock is real and must be left alone.
 *
 * - Dialog / AlertDialog content carry `role="dialog"` / `role="alertdialog"`.
 * - DropdownMenu / ContextMenu / Menubar content carry `data-radix-menu-content`.
 * - Select renders `data-radix-select-viewport` only while open.
 * - Popover / and other popper-based content render inside
 *   `[data-radix-popper-content-wrapper]` while mounted.
 */
const OPEN_OVERLAY_SELECTOR = [
  '[role="dialog"]',
  '[role="alertdialog"]',
  "[data-radix-menu-content]",
  "[data-radix-select-viewport]",
  "[data-radix-popper-content-wrapper]",
].join(",");

/**
 * True when at least one Radix overlay layer is currently mounted in the DOM
 * (open, or animating closed). While one exists, a body pointer-events lock is
 * considered legitimate.
 */
export function hasOpenRadixOverlay(doc: Document = document): boolean {
  return doc.querySelector(OPEN_OVERLAY_SELECTOR) != null;
}

/**
 * If `<body>` is stuck at `pointer-events: none` but no Radix overlay layer is
 * mounted, clear the leaked lock. Returns true if it cleared one.
 */
export function clearLeakedBodyPointerEvents(doc: Document = document): boolean {
  const body = doc.body;
  if (!body) return false;
  if (body.style.pointerEvents !== "none") return false;
  if (hasOpenRadixOverlay(doc)) return false;
  body.style.pointerEvents = "";
  return true;
}

/**
 * Mount once near the app root. Self-heals the Radix body pointer-events leak
 * regardless of which overlay caused it, without interfering with overlays that
 * are legitimately open.
 */
export function useBodyPointerEventsGuard(): void {
  useEffect(() => {
    if (typeof document === "undefined" || !document.body) return;

    const body = document.body;

    // Defer to a microtask so we check *after* React/Radix have finished the
    // current commit (a layer that is mounting in the same tick will already
    // be in the DOM, so we won't clear a lock that's about to be legitimate).
    let scheduled = false;
    const scheduleCheck = () => {
      if (scheduled) return;
      scheduled = true;
      queueMicrotask(() => {
        scheduled = false;
        clearLeakedBodyPointerEvents();
      });
    };

    // 1. Watch the body's own style attribute: catches the moment the lock is
    //    set (and, importantly, any time it's re-set while no overlay is open).
    // 2. Watch body childList/subtree: a leaked lock is left behind precisely
    //    when the offending layer *unmounts* without a further body style
    //    change, so the removal of the layer node is our signal to re-check.
    const observer = new MutationObserver(scheduleCheck);
    observer.observe(body, {
      attributes: true,
      attributeFilter: ["style"],
      childList: true,
      subtree: true,
    });

    // Belt-and-suspenders: re-check on the next user interaction. Even when
    // body is locked, capture-phase listeners on `document` still fire (the
    // event passes through the pointer-events:none body to <html>), so this
    // guarantees interactivity is restored on the very next touch/click.
    const onInteraction = () => clearLeakedBodyPointerEvents();
    const opts: AddEventListenerOptions = { capture: true, passive: true };
    document.addEventListener("pointerdown", onInteraction, opts);
    document.addEventListener("touchstart", onInteraction, opts);
    document.addEventListener("keydown", onInteraction, opts);

    // Run once on mount in case a lock was already leaked before we attached.
    clearLeakedBodyPointerEvents();

    return () => {
      observer.disconnect();
      document.removeEventListener("pointerdown", onInteraction, opts);
      document.removeEventListener("touchstart", onInteraction, opts);
      document.removeEventListener("keydown", onInteraction, opts);
    };
  }, []);
}
